import { ReactNode, createContext, useCallback, useContext } from 'react'

import { EmbroideryModal } from '../../components/embroidery/EmbroideryModal/EmbroideryModal'
import { DtcEmbroideryModal } from '../../components/embroidery/Modal/flows/dtc/DtcEmbroideryModal'
import { useDtcEmbroideryModalState } from '../../components/embroidery/Modal/flows/dtc/useDtcEmbroideryModalState'

import { EmbroideryProvider, UseEmbroideryProps } from './EmbroideryProvider'
import { EMBROIDERY_VERSION_V1, EMBROIDERY_VERSION_V2 } from './embroideryVersion'
import { DtcEmbroideryProvider } from './providers/DtcEmbroideryProvider'
import { EmbroiderySelectionInitialState } from './providers/EmbroiderySelectionProvider'
import { useMakeEmbroideredCartItemV2Input } from './providers/useMakeEmbroideredCartItemV2Input'
import { useEmbroideryVersion } from './useEmbroideryVersion'
import { useMakeEmbroideredCartItem } from './useMakeEmbroideredCartItem'
import {
  MakeEmbroideredCartItemV2Input,
  useMakeEmbroideredCartItemV2,
} from './useMakeEmbroideredCartItemV2'
import { NewLogoInOrder, NewLogoOnPageProvider } from './useNewLogoInOrder'

type Props = {
  /** What the site would give the legacy provider. The new stack takes what it needs from it. */
  initialState: UseEmbroideryProps
  /** The multi-placement selection to start from. The legacy stack seeds from `initialState`. */
  initialSelection?: EmbroiderySelectionInitialState
  /**
   * Called with the multi-placement selection each time it changes, for the site to hand back as
   * `initialSelection` when it remounts. The legacy stack never calls it.
   */
  onSelectionChange?: (selection: EmbroiderySelectionInitialState) => void
  /**
   * The new logo another garment on this page carries before it reaches the cart, on pages that add
   * several garments. The multi-placement logo form offers it in place of a second upload.
   */
  newLogoOnPage?: NewLogoInOrder
  /** Passed to the legacy modal only. */
  isB2BColorSelected?: boolean
  children?: ReactNode
}

type DtcEmbroideryModalState = ReturnType<typeof useDtcEmbroideryModalState>

type EmbroideryModalSlotValue =
  | { flowVersion: typeof EMBROIDERY_VERSION_V1; isB2BColorSelected?: boolean }
  | { flowVersion: typeof EMBROIDERY_VERSION_V2; dtcModal: DtcEmbroideryModalState }

const EmbroideryModalSlotContext = createContext<EmbroideryModalSlotValue | null>(null)

const V2EmbroideryCartInputContext = createContext<MakeEmbroideredCartItemV2Input | undefined>(
  undefined
)

/**
 * What `useMakeEmbroideredCartItemV2` needs to build bag lines. Undefined in the legacy flow, so a
 * site can also use it to tell which cart logic to run.
 */
export const useV2EmbroideryCartInput = () => useContext(V2EmbroideryCartInputContext)

/** The flow `EmbroideryProviderForFlowVersion` mounted. Undefined outside it. */
export const useEmbroideryFlowVersion = () => useContext(EmbroideryModalSlotContext)?.flowVersion

/**
 * Builds the bag lines for a garment and its embroidery with the cart logic of the flow
 * `EmbroideryProviderForFlowVersion` mounted.
 */
export const useMakeEmbroideredCartItemForFlowVersion = () => {
  const makeEmbroideredCartItem = useMakeEmbroideredCartItem()
  const makeEmbroideredCartItemV2 = useMakeEmbroideredCartItemV2()
  const v2CartInput = useV2EmbroideryCartInput()

  return useCallback(
    (props: Parameters<typeof makeEmbroideredCartItem>[0], categoryId: string) =>
      v2CartInput
        ? makeEmbroideredCartItemV2(props, categoryId, v2CartInput)
        : makeEmbroideredCartItem(props, categoryId),
    [makeEmbroideredCartItem, makeEmbroideredCartItemV2, v2CartInput]
  )
}

/**
 * Mounts embroidery for one site: the multi-placement providers and modal when the shopper is in
 * that flow, otherwise the legacy provider and modal. The parts of the page outside the modal read
 * the same contract either way, so this is the only place a site has to choose.
 *
 * The modal renders where the site places `EmbroideryModalSlot`, not here, because the legacy
 * modal reads the variant selection form and the form sits below this provider.
 */
export const EmbroideryProviderForFlowVersion = ({
  initialState,
  initialSelection,
  onSelectionChange,
  newLogoOnPage,
  isB2BColorSelected,
  children,
}: Props) => {
  const flowVersion = useEmbroideryVersion({
    embroideryPurpose: initialState.embroideryPurpose,
  })

  if (flowVersion === EMBROIDERY_VERSION_V2) {
    return (
      <MultiPlacementEmbroidery
        initialState={initialState}
        initialSelection={initialSelection}
        onSelectionChange={onSelectionChange}
        newLogoOnPage={newLogoOnPage}
      >
        {children}
      </MultiPlacementEmbroidery>
    )
  }

  return (
    <EmbroideryProvider initialState={initialState}>
      <EmbroideryModalSlotContext.Provider
        value={{ flowVersion: EMBROIDERY_VERSION_V1, isB2BColorSelected }}
      >
        {children}
      </EmbroideryModalSlotContext.Provider>
    </EmbroideryProvider>
  )
}

/** Renders the modal for the flow version `EmbroideryProviderForFlowVersion` chose. */
export const EmbroideryModalSlot = () => {
  const slot = useContext(EmbroideryModalSlotContext)
  if (!slot) return null

  if (slot.flowVersion === EMBROIDERY_VERSION_V2) {
    const { dtcModal } = slot
    return (
      <DtcEmbroideryModal
        key={dtcModal.openCount}
        isOpen={dtcModal.isOpen}
        initialTypeKey={dtcModal.initialTypeKey}
        onClose={dtcModal.close}
      />
    )
  }

  return <EmbroideryModal isB2BColorSelected={slot.isB2BColorSelected} />
}

const MultiPlacementEmbroidery = ({
  initialState,
  initialSelection,
  onSelectionChange,
  newLogoOnPage,
  children,
}: Props) => {
  const modal = useDtcEmbroideryModalState()

  return (
    <NewLogoOnPageProvider value={newLogoOnPage}>
      <DtcEmbroideryProvider
        productEmbroideryConfig={initialState.productEmbroideryConfig}
        productColorHexCodes={initialState.productColorHexCodes}
        initialSelection={initialSelection}
        onSelectionChange={onSelectionChange}
        onSelectFormKind={modal.selectFormKind}
        onOpenModal={modal.open}
      >
        <V2EmbroideryCartInputFill>
          <EmbroideryModalSlotContext.Provider
            value={{ flowVersion: EMBROIDERY_VERSION_V2, dtcModal: modal }}
          >
            {children}
          </EmbroideryModalSlotContext.Provider>
        </V2EmbroideryCartInputFill>
      </DtcEmbroideryProvider>
    </NewLogoOnPageProvider>
  )
}

const V2EmbroideryCartInputFill = ({ children }: { children?: ReactNode }) => {
  const input = useMakeEmbroideredCartItemV2Input()
  return (
    <V2EmbroideryCartInputContext.Provider value={input}>
      {children}
    </V2EmbroideryCartInputContext.Provider>
  )
}
